import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  getPublicYouTubeRankings,
  PublicRankingResponse,
  PublicRankingVideo,
} from "./rankingApi";
import styles from "./YouTubeRankingsPage.module.css";
import {
  COOKIE_CONSENT_CHANGED_EVENT,
  getRankingPreferences,
  saveRankingPreferences,
} from "../utils/userPreferences";
import {
  getYouTubeRankingContent,
  YOUTUBE_RANKING_COUNTRY_CODES,
} from "../youtubeRankingData";
import {
  getMicrosoftStoreUrl,
  trackMicrosoftStoreClick,
} from "../utils/microsoftStore";

type RankingTab = "daily" | "weekly" | "rising";
type DurationGroup = "under_3m" | "over_3m";

const CATEGORIES = ["1", "2", "10", "15", "17", "19", "20", "22", "23", "24", "25", "26", "27", "28", "29"] as const;

const ITEMS_PER_PAGE = 20;

function getInitialRankingPreferences() {
  const savedPreferences = getRankingPreferences();
  const countryCode = savedPreferences?.countryCode ?? "KR";
  const categoryId = savedPreferences?.categoryId ?? "10";

  return {
    countryCode: YOUTUBE_RANKING_COUNTRY_CODES.includes(countryCode) ? countryCode : "KR",
    categoryId: (CATEGORIES as readonly string[]).includes(categoryId) ? categoryId : "10",
  };
}

function formatNumber(value: number | null, locale: string): string {
  if (value === null) {
    return "-";
  }
  return new Intl.NumberFormat(locale).format(value);
}

function formatChange(value: number | null, locale: string): string {
  if (value === null) {
    return "-";
  }
  const prefix = value > 0 ? "+" : "";
  return `${prefix}${new Intl.NumberFormat(locale).format(value)}`;
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await Promise.race([
        navigator.clipboard.writeText(value),
        new Promise<void>((_, reject) => {
          window.setTimeout(() => reject(new Error("Clipboard timeout")), 700);
        }),
      ]);
      return;
    } catch (error) {
      console.warn("Clipboard API was unavailable; using the copy fallback.", error);
    }
  }

  const textArea = document.createElement("textarea");
  textArea.value = value;
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  document.body.removeChild(textArea);
}

const YouTubeRankingsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language ?? "en";
  const rankingContent = getYouTubeRankingContent(locale);
  const [countryCode, setCountryCode] = useState("KR");
  const [categoryId, setCategoryId] = useState("10");
  const [activeTab, setActiveTab] = useState<RankingTab>("daily");
  const [durationGroup, setDurationGroup] = useState<DurationGroup>("under_3m");
  const [data, setData] = useState<PublicRankingResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedVideoId, setCopiedVideoId] = useState<string | null>(null);

  const period = activeTab === "weekly" ? "weekly" : "daily";

  useEffect(() => {
    const preferences = getInitialRankingPreferences();
    setCountryCode(preferences.countryCode);
    setCategoryId(preferences.categoryId);
  }, []);

  useEffect(() => {
    const savePreferences = () => {
      saveRankingPreferences({ countryCode, categoryId });
    };

    savePreferences();
    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, savePreferences);
    return () => {
      window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, savePreferences);
    };
  }, [categoryId, countryCode]);

  useEffect(() => {
    let isCancelled = false;
    setIsLoading(true);
    setError(null);
    setCurrentPage(1);

    getPublicYouTubeRankings(countryCode, categoryId, durationGroup, period)
      .then((response) => {
        if (!isCancelled) {
          setData(response);
        }
      })
      .catch((loadError) => {
        if (!isCancelled) {
          console.error("Failed to load YouTube rankings:", loadError);
          setData(null);
          setError(t("youtube_rankings.page.no_data"));
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [categoryId, countryCode, durationGroup, period, t]);

  const visibleVideos = useMemo(() => {
    if (!data) {
      return [];
    }
    if (activeTab !== "rising") {
      return [...data.videos].sort((a, b) => a.rank - b.rank);
    }
    return data.videos
      .filter((video) => video.isRising)
      .sort((a, b) => (a.risingRank ?? 999) - (b.risingRank ?? 999));
  }, [activeTab, data]);

  const totalPages = Math.max(1, Math.ceil(visibleVideos.length / ITEMS_PER_PAGE));
  const pageVideos = visibleVideos.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, durationGroup]);

  const handleCopy = (video: PublicRankingVideo) => {
    setCopiedVideoId(video.videoId);
    copyText(video.youtubeUrl).catch((copyError) => {
      console.error("Failed to copy the YouTube link:", copyError);
    });
    window.setTimeout(() => setCopiedVideoId(null), 1600);
  };

  const countryNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([locale], { type: "region" });
    } catch (displayNameError) {
      console.warn("Localized country names are unavailable.", displayNameError);
      return null;
    }
  }, [locale]);

  const countryLabel = (countryCodeValue: string) =>
    countryNames?.of(countryCodeValue) ?? countryCodeValue;

  const categoryLabel = (categoryIdValue: string) =>
    t(`youtube_rankings.categories.${categoryIdValue}`);

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <span className={styles.eyebrow}>YOUTUBE TREND DISCOVERY</span>
        <h1>{rankingContent.heading}</h1>
        <p>{rankingContent.description}</p>
      </section>

      <section className={styles.controls} aria-label={t("youtube_rankings.page.filters")}>
        <label>
          <span>{t("youtube_rankings.page.country")}</span>
          <select value={countryCode} onChange={(event) => setCountryCode(event.target.value)}>
            {YOUTUBE_RANKING_COUNTRY_CODES.map((code) => (
              <option key={code} value={code}>
                {countryLabel(code)} ({code})
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>{t("youtube_rankings.page.category")}</span>
          <select value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
            {CATEGORIES.map((id) => (
              <option key={id} value={id}>
                {categoryLabel(id)}
              </option>
            ))}
          </select>
        </label>
        <div className={styles.captureInfo}>
          <span>{t("youtube_rankings.page.snapshot")}</span>
          <strong>
            {period === "weekly" && data?.periodStartDate ?
              `${data.periodStartDate} ~ ${data.periodEndDate}` :
              `${data?.capturedForDate || "-"} UTC`}
          </strong>
        </div>
      </section>

      <section className={styles.supportedCountries} aria-labelledby="supported-countries-title">
        <div className={styles.supportedCountriesHeader}>
          <h2 id="supported-countries-title">{rankingContent.supportedCountriesTitle}</h2>
          <p>{rankingContent.supportedCountriesIntro}</p>
        </div>
        <ul>
          {YOUTUBE_RANKING_COUNTRY_CODES.map((code) => (
            <li key={code}>
              {countryLabel(code)} <span>{code}</span>
            </li>
          ))}
        </ul>
      </section>

      <div className={styles.tabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "daily"}
          className={activeTab === "daily" ? styles.activeTab : ""}
          onClick={() => setActiveTab("daily")}
        >
          {t("youtube_rankings.page.popular")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "weekly"}
          className={activeTab === "weekly" ? styles.activeTab : ""}
          onClick={() => setActiveTab("weekly")}
        >
          {t("youtube_rankings.page.weekly")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "rising"}
          className={activeTab === "rising" ? styles.activeTab : ""}
          onClick={() => setActiveTab("rising")}
        >
          {t("youtube_rankings.page.rising")}
        </button>
      </div>

      <div className={styles.durationFilters} aria-label={t("youtube_rankings.page.duration")}>
        <span>{t("youtube_rankings.page.duration")}</span>
        <button
          type="button"
          className={durationGroup === "under_3m" ? styles.activeDuration : ""}
          onClick={() => setDurationGroup("under_3m")}
        >
          {t("youtube_rankings.page.under_3m")}
        </button>
        <button
          type="button"
          className={durationGroup === "over_3m" ? styles.activeDuration : ""}
          onClick={() => setDurationGroup("over_3m")}
        >
          {t("youtube_rankings.page.over_3m")}
        </button>
      </div>

      {isLoading ? (
        <div className={styles.statePanel}>{t("youtube_rankings.page.loading")}</div>
      ) : error ? (
        <div className={`${styles.statePanel} ${styles.errorPanel}`}>{error}</div>
      ) : pageVideos.length === 0 ? (
        <div className={styles.statePanel}>
          {t("youtube_rankings.page.no_rising")}
        </div>
      ) : (
        <section className={styles.grid} aria-live="polite">
          {pageVideos.map((video) => (
            <article key={video.videoId} className={styles.card}>
              <a className={styles.thumbnailLink} href={video.youtubeUrl} target="_blank" rel="noreferrer">
                {video.thumbnailUrl ? (
                  <img src={video.thumbnailUrl} alt="" loading="lazy" />
                ) : (
                  <div className={styles.thumbnailFallback}>YouTube</div>
                )}
                <span className={styles.rankBadge}>
                  {activeTab === "rising" ? `HOT ${video.risingRank}` : `#${video.rank}`}
                </span>
                {video.isNewEntry ? <span className={styles.newBadge}>NEW</span> : null}
              </a>

              <div className={styles.cardBody}>
                <a className={styles.videoTitle} href={video.youtubeUrl} target="_blank" rel="noreferrer">
                  {video.title}
                </a>
                <div className={styles.videoMeta}>
                  <p className={styles.channelName}>{video.channelTitle ?? "-"}</p>
                  <span className={styles.formatBadge}>
                    {video.durationGroup === "under_3m" ?
                      t("youtube_rankings.page.under_3m") :
                      t("youtube_rankings.page.over_3m")}
                  </span>
                </div>

                <div className={styles.metrics}>
                  <div>
                    <span>{t("youtube_rankings.page.views")}</span>
                    <strong>{formatNumber(video.viewCount, locale)}</strong>
                  </div>
                  <div>
                    <span>
                      {period === "weekly" ?
                        t("youtube_rankings.page.weekly_score") :
                        t("youtube_rankings.page.view_change")}
                    </span>
                    <strong className={(video.viewCountChange ?? 0) > 0 ? styles.positive : ""}>
                      {period === "weekly" ?
                        formatNumber(video.weeklyScore, locale) :
                        formatChange(video.viewCountChange, locale)}
                    </strong>
                  </div>
                  <div>
                    <span>
                      {period === "weekly" ?
                        t("youtube_rankings.page.days_ranked") :
                        t("youtube_rankings.page.rank_change")}
                    </span>
                    <strong className={(video.rankChange ?? 0) > 0 ? styles.positive : ""}>
                      {period === "weekly" ?
                        formatNumber(video.appearanceDays, locale) :
                        formatChange(video.rankChange, locale)}
                    </strong>
                  </div>
                </div>

                <div className={styles.actions}>
                  <a href={video.youtubeUrl} target="_blank" rel="noreferrer">
                    {t("youtube_rankings.page.watch")}
                  </a>
                  <button type="button" onClick={() => handleCopy(video)}>
                    {copiedVideoId === video.videoId ?
                      t("youtube_rankings.page.copied") :
                      t("youtube_rankings.page.copy")}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </section>
      )}

      {!isLoading && !error && totalPages > 1 ? (
        <nav className={styles.pagination} aria-label={t("youtube_rankings.page.pagination")}>
          <button type="button" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
            {t("youtube_rankings.page.previous")}
          </button>
          <span>{currentPage} / {totalPages}</span>
          <button type="button" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
            {t("youtube_rankings.page.next")}
          </button>
        </nav>
      ) : null}

      <aside className={styles.sourceNote} aria-label={t("youtube_rankings.page.source")}>
        <div className={styles.sourceDetails}>
          <p>{t("youtube_rankings.page.source")}</p>
          <p>{t("youtube_rankings.page.weekly_source")}</p>
          <p className={styles.availabilityNote}>
            {t("youtube_rankings.page.availability_note", {
              defaultValue: "This ranking is based on the YouTube Data API mostPopular chart. Availability varies by country and category, with up to 100 videos shown.",
            })}
          </p>
        </div>
        <div className={styles.sourceLinks}>
          <a
            href="https://developers.google.com/youtube/v3/docs/videos/list"
            target="_blank"
            rel="noreferrer"
          >
            YouTube Data API
          </a>
          <a href="https://www.youtube.com/t/terms" target="_blank" rel="noreferrer">
            YouTube Terms of Service
          </a>
        </div>
      </aside>

      <section className={styles.autovidCta}>
        <div className={styles.ctaContent}>
          <span>{t("youtube_rankings.cta.eyebrow")}</span>
          <h2>{t("youtube_rankings.cta.title")}</h2>
          <p>{t("youtube_rankings.cta.description")}</p>
        </div>
        <div className={styles.ctaActions}>
          <div className={styles.ctaSecondaryActions}>
            <Link to={`/${locale}/features`}>
              {t("youtube_rankings.cta.features")}
            </Link>
            <Link to={`/${locale}/sample-videos`}>
              {t("youtube_rankings.cta.samples")}
            </Link>
          </div>
          <a
            className={styles.ctaDownload}
            href={getMicrosoftStoreUrl("youtube_rankings")}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackMicrosoftStoreClick("youtube_rankings")}
          >
            {t("youtube_rankings.cta.download")}
          </a>
        </div>
      </section>

    </div>
  );
};

export default YouTubeRankingsPage;
